import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Slider,
  Typography,
} from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import {
  CloudUploadRounded,
  CropLandscapeRounded,
  CropPortraitRounded,
} from "@material-ui/icons";
import PropTypes from "prop-types";
import * as React from "react";

import { InfoTooltip } from "../../theme";

const StyledCheckContainer = styled("label")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  backgroundColor: theme.palette.grey[200],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  cursor: "pointer",
  "& :first-child": {
    marginTop: -4,
  },
  "& span": {
    ...theme.typography.caption,
    display: "block",
  },
}));

const Cropper = React.lazy(() => import("react-easy-crop"));

const ACCEPTED_TYPES = "image/png, image/jpg, image/jpeg";

const visuallyHiddenStyle = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

function readFileToUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result));
    reader.addEventListener("error", reject);
    reader.readAsDataURL(file);
  });
}

function getImageFromFileInputEvent({ target }) {
  const { files } = target;
  return [...files].find((file) => file.type.startsWith("image/"));
}

function getImageFromDropEvent({ dataTransfer }) {
  if (dataTransfer.items) {
    const found = [...dataTransfer.items].find(
      (item) => item.kind === "file" && item.type.startsWith("image/"),
    );
    return found?.getAsFile?.();
  }
  return [...dataTransfer.files].find((file) => file.type.startsWith("image/"));
}

async function uploadCustomerImage({ imageFile, cropSettings, token }) {
  const headers = {
    "Content-Type": imageFile.type,
    "X-Image-Crop-Width": cropSettings.width,
    "X-Image-Crop-Height": cropSettings.height,
    "X-Image-Crop-X": cropSettings.x,
    "X-Image-Crop-Y": cropSettings.y,
  };

  if (token) {
    headers["x-user-token"] = token;
  }

  const response = await window.fetch(`/api/user/customerImageUpload`, {
    method: "POST",
    body: imageFile,
    headers,
  });

  if (!response.headers.get("content-type")?.includes("application/json")) {
    throw new Error("Something went wrong, please try again");
  }

  const json = await response.json();

  if (!json.success) {
    throw new Error(json.error?.message ?? "Upload failed");
  }

  return json.image;
}

const ALLOWED_LIST = [
  "Fan art",
  "Official character art",
  "Personal photos",
  "Custom text requests",
];

const NOT_ALLOWED_LIST = [
  "Nudity or sexual content",
  "Graphic violence",
  "Hate symbols, slurs, or offensive imagery",
  "Real people depicted in compromising situations",
  "Third-party IP you don't own (logos, screenshots, branded content)",
];

const CHECKBOX_TEXT =
  "I confirm my image follows the guidelines below. I understand that rejected uploads are not eligible for a refund. Refunds are only issued if my selected talent is unavailable to complete the signing.";

const CustomerImageUpload = React.memo(function CustomerImageUpload({
  aspectRatio,
  onUploadComplete,
  token,
  hasUploadedImage,
  onChangeImage,
  showTerms = true,
}) {
  const [imageFile, setImageFile] = React.useState(null);
  const [imageSrc, setImageSrc] = React.useState(null);
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [cropSettings, setCropSettings] = React.useState({});
  const [isUploading, setIsUploading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [orientation, setOrientation] = React.useState("portrait");
  const [termsAccepted, setTermsAccepted] = React.useState(false);

  const isSquare = aspectRatio === 1;
  const cropAspect = isSquare
    ? 1
    : orientation === "landscape"
    ? 1 / aspectRatio
    : aspectRatio;

  const reset = React.useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropSettings({});
    setErrorMessage("");
    setOrientation("portrait");
    setTermsAccepted(false);
  }, []);

  const handleFile = React.useCallback(
    async (file) => {
      if (!file) return;
      reset();
      const fileUrl = await readFileToUrl(file);
      setImageSrc(fileUrl);
      setImageFile(file);
    },
    [reset],
  );

  const onFileInputChange = React.useCallback(
    async (evt) => {
      const file = getImageFromFileInputEvent(evt);
      await handleFile(file);
      if (evt && evt.target) {
        // eslint-disable-next-line no-param-reassign
        evt.target.value = "";
      }
    },
    [handleFile],
  );

  const onDrop = React.useCallback(
    async (evt) => {
      evt.preventDefault();
      setIsDragOver(false);
      const file = getImageFromDropEvent(evt);
      await handleFile(file);
    },
    [handleFile],
  );

  const onCropComplete = React.useCallback((_, croppedAreaPixels) => {
    setCropSettings(croppedAreaPixels);
  }, []);

  const onUpload = async () => {
    try {
      setIsUploading(true);
      setErrorMessage("");

      const image = await uploadCustomerImage({
        imageFile,
        cropSettings,
        token,
      });

      onUploadComplete({
        imageUrl: image.imageUrl,
        originalImageUrl: image.originalImageUrl,
        croppedOriginalImageUrl: image.croppedOriginalImageUrl,
      });

      setImageSrc(null);
      setImageFile(null);
      setIsUploading(false);
    } catch (err) {
      setErrorMessage(err.message);
      setIsUploading(false);
    }
  };

  const handleChangeImage = () => {
    onChangeImage?.();
    setImageSrc(null);
    setImageFile(null);
    reset();
  };

  const uploadDisabled =
    isUploading || !cropSettings.width || (showTerms && !termsAccepted);

  if (hasUploadedImage) {
    return (
      <StyledContainer>
        <StyledLabel variant="body2">Your Image</StyledLabel>
        <Box display="flex" alignItems="center" gridGap="8px">
          <Typography variant="body2" color="textSecondary">
            Image uploaded
          </Typography>
          <Button size="small" onClick={handleChangeImage}>
            Change
          </Button>
        </Box>
      </StyledContainer>
    );
  }

  if (imageSrc) {
    return (
      <StyledContainer>
        <StyledLabel variant="body2">Crop Your Image</StyledLabel>
        <React.Suspense
          fallback={
            <Box display="flex" justifyContent="center" p={2} role="status">
              <CircularProgress size={24} aria-label="Loading crop editor" />
            </Box>
          }
        >
          <StyledCropEditor>
            <StyledCropContainer
              onDragLeave={() => setIsDragOver(false)}
              onDragOver={(evt) => {
                evt.preventDefault();
                setIsDragOver(true);
              }}
              onDrop={onDrop}
            >
              <Cropper
                aspect={cropAspect}
                crop={crop}
                image={imageSrc}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                zoom={zoom}
                maxZoom={3}
              />
            </StyledCropContainer>

            <Box display="flex" flexDirection="column" gridGap={8} p={1.5}>
              <Box display="flex" justifyContent="space-between" gridGap={16}>
                {!isSquare && (
                  <StyledOrientationToggle
                    role="group"
                    aria-label="Crop orientation"
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      className={orientation === "portrait" ? "active" : ""}
                      onClick={() => setOrientation("portrait")}
                      startIcon={<CropPortraitRounded />}
                      disabled={isUploading}
                      aria-pressed={orientation === "portrait"}
                    >
                      Portrait
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      className={orientation === "landscape" ? "active" : ""}
                      onClick={() => setOrientation("landscape")}
                      startIcon={<CropLandscapeRounded />}
                      disabled={isUploading}
                      aria-pressed={orientation === "landscape"}
                    >
                      Landscape
                    </Button>
                  </StyledOrientationToggle>
                )}

                <Slider
                  value={zoom}
                  onChange={(_, z) => {
                    if (!Number.isFinite(z)) return;
                    setZoom(z);
                  }}
                  max={3}
                  min={1}
                  step={0.1}
                  disabled={isUploading}
                  aria-label="Zoom"
                  getAriaValueText={(value) => `${value.toFixed(1)}x zoom`}
                />
              </Box>

              {showTerms && (
                <StyledTermsBox>
                  <StyledCheckContainer>
                    <Checkbox
                      size="small"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      disabled={isUploading}
                    />
                    <span>{CHECKBOX_TEXT}</span>
                  </StyledCheckContainer>
                  <Typography
                    variant="caption"
                    component="div"
                    style={{
                      marginTop: 8,
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                    }}
                    color="textSecondary"
                  >
                    <div>
                      <strong>What&apos;s allowed:</strong>
                      <ul style={{ margin: "4px 0 8px", paddingLeft: 20 }}>
                        {ALLOWED_LIST.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <strong>Not allowed:</strong>
                      <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                        {NOT_ALLOWED_LIST.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </Typography>
                </StyledTermsBox>
              )}

              {Boolean(errorMessage) && (
                <Typography variant="caption" color="error" role="alert">
                  {errorMessage}
                </Typography>
              )}

              <Box display="flex" gridGap={8}>
                <Button
                  fullWidth
                  onClick={() => {
                    setImageSrc(null);
                    setImageFile(null);
                    reset();
                  }}
                  disabled={isUploading}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  onClick={onUpload}
                  disabled={uploadDisabled}
                  aria-label={isUploading ? "Uploading image" : undefined}
                >
                  {isUploading ? (
                    <CircularProgress size="1rem" aria-hidden="true" />
                  ) : (
                    "Upload"
                  )}
                </Button>
              </Box>
            </Box>
          </StyledCropEditor>
        </React.Suspense>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <Box display="flex" alignItems="center">
        <StyledLabel variant="body2">Your Image</StyledLabel>
        <InfoTooltip
          leaveTouchDelay={6000}
          size="small"
          title="About Custom Image Upload"
          content="Images uploaded for personalization will be used solely to fulfill your order. Streamily will not sell, share, publish, or use your uploaded image for marketing, promotional purposes, or any other customer’s order. Customers may only upload artwork they own or have the appropriate rights or permission to use. AI-generated artwork is not permitted. Uploaded images may be rejected by Streamily or the talent if they violate these guidelines or are otherwise deemed inappropriate for signing."
        />
      </Box>

      <StyledDropZone
        htmlFor="customer-image-upload-input"
        className={isDragOver ? "drag-over" : ""}
        onDragLeave={() => setIsDragOver(false)}
        onDragOver={(evt) => {
          evt.preventDefault();
          setIsDragOver(true);
        }}
        onDrop={onDrop}
      >
        <CloudUploadRounded
          color="action"
          style={{ fontSize: 40 }}
          aria-hidden="true"
        />
        <Typography variant="body2" color="textSecondary">
          Drag and drop your image here, or click to select
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Minimum 970 x 970px. PNG or JPG.
        </Typography>
        <input
          accept={ACCEPTED_TYPES}
          id="customer-image-upload-input"
          onChange={onFileInputChange}
          style={visuallyHiddenStyle}
          type="file"
          aria-label="Upload your image (PNG or JPG, minimum 800 by 800 pixels)"
        />
      </StyledDropZone>

      {Boolean(errorMessage) && (
        <Typography variant="caption" color="error" role="alert">
          {errorMessage}
        </Typography>
      )}
    </StyledContainer>
  );
});

CustomerImageUpload.propTypes = {
  aspectRatio: PropTypes.number.isRequired,
  onUploadComplete: PropTypes.func.isRequired,
  token: PropTypes.string,
  hasUploadedImage: PropTypes.bool,
  onChangeImage: PropTypes.func,
  showTerms: PropTypes.bool,
};

const StyledContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.interFontStack,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[800],
}));

const StyledDropZone = styled("label")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(3),
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: theme.spacing(1),
  cursor: "pointer",
  transition: "border-color 0.2s, box-shadow 0.2s",
  "&:focus-within": {
    borderColor: theme.palette.secondary.main,
    boxShadow: `0 0 0 2px ${theme.palette.secondary.main}40`,
  },
  "&.drag-over": {
    borderColor: theme.palette.secondary.main,
  },
}));

const StyledCropEditor = styled("div")(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.spacing(1),
  overflow: "hidden",
  backgroundColor: theme.palette.grey[50],
}));

const StyledCropContainer = styled("div")({
  position: "relative",
  width: "100%",
  height: 300,
});

const StyledOrientationToggle = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  "& .MuiButton-root": {
    borderColor: theme.palette.grey[300],
    color: theme.palette.text.secondary,
  },
  "& .MuiButton-root.active": {
    borderColor: theme.palette.secondary.main,
    color: theme.palette.secondary.main,
    backgroundColor: theme.palette.secondary.extraLight,
  },
}));

const StyledTermsBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(1),
  maxHeight: 200,
  overflowY: "auto",
}));

export default CustomerImageUpload;
